import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getProfile, getWorkoutChatHistory, lockWorkoutProgram, postWorkoutCoachMessage } from '../api';
import { stripPersonaTags } from '../lib/strip-persona-tags';
import type {
  WorkoutChatHistoryMessage,
  WorkoutCoachPrescription,
  WorkoutCoachReply,
} from '../types';

type UserMsg = { kind: 'user'; content: string };
type CoachMsg = { kind: 'coach'; reply: WorkoutCoachReply };
type PrescriptionMsg = { kind: 'prescription'; prescription: WorkoutCoachPrescription };
type ThinkingMsg = { kind: 'thinking' };
type Msg = UserMsg | CoachMsg | PrescriptionMsg | ThinkingMsg;

// Per-prescription chip state. Starts fully locked (all session keys in `locked`).
// Tap a chip to move it to edit-loop (remove from `locked`); tap again to re-lock.
// Pressing × on an edit-loop chip moves it to `removed` (hidden from chip list).
type ChipState = { locked: Set<string>; removed: Set<string> };

const PERSONA_VARIANTS: Record<string, string> = {
  good_cop: ' bubble--good-cop',
  bad_cop: ' bubble--bad-cop',
  therapist: ' bubble--therapist',
  scientist: ' bubble--scientist',
  nonna: ' bubble--nonna',
  drill_sergeant: ' bubble--drill-sergeant',
};

function bubbleVariant(slug: string): string {
  return PERSONA_VARIANTS[slug] ?? '';
}

const DEFAULT_GREETING: CoachMsg = {
  kind: 'coach',
  reply: {
    persona: 'system',
    content:
      "Tell me what you want to train, what equipment you've got, and any injuries to work around. I'll write you a program.",
  },
};

function historyToMessages(history: WorkoutChatHistoryMessage[]): Msg[] {
  if (history.length === 0) return [DEFAULT_GREETING];
  return history.map<Msg>((row) => {
    if (row.role === 'user') {
      return { kind: 'user', content: row.content };
    }
    return {
      kind: 'coach',
      reply: {
        persona: row.persona ?? 'system',
        content: row.content,
      },
    };
  });
}

export function WorkoutChat() {
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();
  const projectId = params.id ?? '';
  const [messages, setMessages] = useState<Msg[]>([DEFAULT_GREETING]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [locked, setLocked] = useState<boolean | null>(null); // null while loading
  const [chipStates, setChipStates] = useState<Map<string, ChipState>>(new Map());
  const [lockingProgramId, setLockingProgramId] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    Promise.all([
      getProfile(),
      getWorkoutChatHistory(projectId).catch(() => [] as WorkoutChatHistoryMessage[]),
    ])
      .then(([profile, history]) => {
        if (cancelled) return;
        setLocked(!(profile?.coach_workout_enabled ?? true));
        setMessages(historyToMessages(history));
      })
      .catch(() => {
        if (!cancelled) setLocked(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  // send() accepts optional explicitText for programmatic sends (edit-loop routing).
  // When called without explicitText it reads + clears the input state as normal.
  const send = async (explicitText?: string): Promise<void> => {
    const text = (explicitText ?? input).trim();
    if (!text || sending || locked || !projectId) return;
    if (!explicitText) setInput('');
    setError(null);
    setSending(true);
    setMessages((m) => [...m, { kind: 'user', content: text }, { kind: 'thinking' }]);
    try {
      const res = await postWorkoutCoachMessage(projectId, text);
      if ('locked' in res) {
        setMessages((m) => m.filter((x) => x.kind !== 'thinking'));
        setLocked(true);
        return;
      }
      setMessages((m) => {
        const next = m.filter((x) => x.kind !== 'thinking');
        next.push({ kind: 'coach', reply: res.reply });
        if (res.prescription) {
          next.push({ kind: 'prescription', prescription: res.prescription });
        }
        return next;
      });
      if (res.prescription) {
        const keys = Object.keys(res.prescription.program_json.exercises);
        setChipStates((prev) => {
          const next = new Map(prev);
          next.set(res.prescription!.program_id, {
            locked: new Set(keys),
            removed: new Set(),
          });
          return next;
        });
      }
    } catch (err) {
      setMessages((m) => m.filter((x) => x.kind !== 'thinking'));
      setError(err instanceof Error ? err.message : 'Coach unavailable');
    } finally {
      setSending(false);
    }
  };

  function toggleChip(programId: string, key: string): void {
    setChipStates((prev) => {
      const state = prev.get(programId);
      if (!state) return prev;
      const newLocked = new Set(state.locked);
      if (newLocked.has(key)) {
        newLocked.delete(key); // locked → edit_loop
      } else {
        newLocked.add(key); // edit_loop → locked
      }
      const next = new Map(prev);
      next.set(programId, { ...state, locked: newLocked });
      return next;
    });
  }

  function removeSession(programId: string, key: string): void {
    setChipStates((prev) => {
      const state = prev.get(programId);
      if (!state) return prev;
      const newLocked = new Set(state.locked);
      const newRemoved = new Set(state.removed);
      newLocked.delete(key);
      newRemoved.add(key);
      const next = new Map(prev);
      next.set(programId, { locked: newLocked, removed: newRemoved });
      return next;
    });
  }

  const handleLockIn = async (prescription: WorkoutCoachPrescription): Promise<void> => {
    const state = chipStates.get(prescription.program_id);
    if (!state) return;
    const locked_sessions = Array.from(state.locked);
    const removed_sessions = Array.from(state.removed);
    if (locked_sessions.length === 0) {
      setError('Select at least one session to lock in.');
      return;
    }
    setLockingProgramId(prescription.program_id);
    setError(null);
    try {
      const result = await lockWorkoutProgram(projectId, prescription.program_id, {
        locked_sessions,
        removed_sessions,
      });
      // Clear chip state — card now renders "Locked in" marker
      setChipStates((prev) => {
        const next = new Map(prev);
        next.delete(prescription.program_id);
        return next;
      });
      setLockingProgramId(null);
      // Route any edit-loop sessions back to coach automatically
      for (const sessionKey of result.edit_loop_sessions) {
        await send(`What would you like to change about ${sessionKey}?`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to lock program');
      setLockingProgramId(null);
    }
  };

  return (
    <div className="shell" style={{ display: 'flex', flexDirection: 'column' }}>
      <header className="rise" style={{ marginBottom: 8 }}>
        <p className="eyebrow">Workout coach</p>
        <h1 className="h1">Program chat</h1>
      </header>
      <div ref={scrollRef} className="chat" style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {messages.map((m, i) => {
          if (m.kind === 'user') {
            return (
              <div key={i} className="bubble bubble--user">{m.content}</div>
            );
          }
          if (m.kind === 'thinking') {
            return (
              <div key={i} className="bubble bubble--coach bubble--thinking">Thinking</div>
            );
          }
          if (m.kind === 'prescription') {
            const p = m.prescription;
            const state = chipStates.get(p.program_id);
            const allKeys = Object.keys(p.program_json.exercises);
            const exerciseCount = Object.values(p.program_json.exercises).reduce(
              (acc, arr) => acc + arr.length,
              0,
            );
            const isLocking = lockingProgramId === p.program_id;
            return (
              <div
                key={i}
                className="card card--elevated rise"
                style={{ margin: '8px 0' }}
              >
                <p className="eyebrow">Proposed program</p>
                <p className="h3" style={{ marginTop: 4 }}>
                  {p.program_json.split_type} · {p.program_json.sessions_per_week}/wk
                </p>
                <p className="muted" style={{ marginTop: 6, fontSize: 13 }}>
                  {allKeys.length} session{allKeys.length === 1 ? '' : 's'}, {exerciseCount} exercise
                  {exerciseCount === 1 ? '' : 's'} total
                </p>
                <p className="muted" style={{ marginTop: 8, fontSize: 13, lineHeight: 1.55 }}>
                  {p.reasoning}
                </p>
                {state ? (
                  <>
                    <div className="chips" style={{ marginTop: 12 }}>
                      {allKeys
                        .filter((k) => !state.removed.has(k))
                        .map((key) => {
                          const isSessionLocked = state.locked.has(key);
                          return (
                            <span
                              key={key}
                              style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                            >
                              <button
                                type="button"
                                className={`chip${isSessionLocked ? ' chip--selected' : ''}`}
                                onClick={() => { toggleChip(p.program_id, key); }}
                              >
                                {key}
                              </button>
                              {!isSessionLocked ? (
                                <button
                                  type="button"
                                  className="chip__remove"
                                  aria-label={`Remove ${key}`}
                                  onClick={() => { removeSession(p.program_id, key); }}
                                >
                                  ×
                                </button>
                              ) : null}
                            </span>
                          );
                        })}
                    </div>
                    <button
                      type="button"
                      className="btn btn--primary btn--full"
                      style={{ marginTop: 12 }}
                      disabled={isLocking || state.locked.size === 0}
                      onClick={() => { void handleLockIn(p); }}
                    >
                      {isLocking ? 'Locking in…' : 'Lock in'}
                    </button>
                  </>
                ) : (
                  <p className="eyebrow" style={{ marginTop: 10, color: 'var(--color-accent)' }}>
                    Locked in
                  </p>
                )}
              </div>
            );
          }
          return (
            <div key={i} className={`bubble bubble--coach${bubbleVariant(m.reply.persona)}`}>
              {m.reply.persona !== 'system' ? (
                <div className="bubble__persona">
                  <span>{m.reply.persona}</span>
                </div>
              ) : null}
              <div>{stripPersonaTags(m.reply.content)}</div>
            </div>
          );
        })}
      </div>
      {error ? <div className="errorbar rise" style={{ marginTop: 12 }}>{error}</div> : null}
      {locked ? (
        <div className="card card--elevated rise" style={{ marginTop: 12 }}>
          <p style={{ margin: 0, fontWeight: 600 }}>Workout coach is off.</p>
          <p className="muted" style={{ margin: '4px 0 12px', fontSize: 14, lineHeight: 1.5 }}>
            Toggle on from the coach page to chat.
          </p>
          <button type="button" className="btn btn--primary" onClick={() => navigate('/coach')}>
            Open coach page
          </button>
        </div>
      ) : (
        <div className="chat__composer rise">
          <input
            className="input"
            placeholder="What do you want to train?"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void send();
              }
            }}
          />
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => void send()}
            disabled={sending || input.trim().length === 0}
          >
            Send
          </button>
        </div>
      )}
    </div>
  );
}
